import ExcelJS from "exceljs"

const normalizeText = value => {
  if (value == null) return ""
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
}

const cellValue = cell => {
  if (!cell || cell.value == null) return ""
  if (typeof cell.value === "object" && cell.value.text != null) {
    return String(cell.value.text).trim()
  }
  return String(cell.value).trim()
}

export const parseComplemento = (comp, defaultBloco = "1") => {
  const raw = String(comp || "").trim().toUpperCase()

  if (!raw) {
    return {
      valid: false,
      error:
        "COMP vazio. Preencha a coluna COMP com AP101B1 (com bloco) ou AP201 (sem bloco).",
    }
  }

  const withBlock = raw.match(/^AP(\d+)B(\d+)$/i)
  if (withBlock) {
    const aptNum = withBlock[1]
    const block = withBlock[2]
    return {
      valid: true,
      IM_COMPLEMENTO: `APT${block} ${aptNum}`,
      IM_BLOCO: block,
    }
  }

  const noBlock = raw.match(/^AP(\d+)$/i)
  if (noBlock) {
    return {
      valid: true,
      IM_COMPLEMENTO: `APTO ${noBlock[1]}`,
      IM_BLOCO: null,
    }
  }

  return {
    valid: false,
    error: `COMP inválido: "${raw}". Use AP101B1 (com bloco) ou AP201 (sem bloco). Linhas MACRO são ignoradas.`,
  }
}

export const buildCdImovel = segments => {
  return segments
    .map(seg => String(seg || "").trim())
    .filter(seg => seg.length > 0)
    .join("")
}

const scanRowsForColumns = (sheet, maxRow) => {
  let compCol = null
  let hdCol = null
  let lastHeaderRow = 0

  for (let rowNum = 1; rowNum <= maxRow; rowNum++) {
    const row = sheet.getRow(rowNum)
    row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      const text = normalizeText(cellValue(cell))

      if (text.includes("comp") && compCol == null) {
        compCol = colNumber
        lastHeaderRow = Math.max(lastHeaderRow, rowNum)
      }

      if (
        (text.includes("hd") || text.includes("medidor") || text === "n hd") &&
        hdCol == null
      ) {
        hdCol = colNumber
        lastHeaderRow = Math.max(lastHeaderRow, rowNum)
      }
    })
  }

  return { compCol, hdCol, lastHeaderRow }
}

const findColumnIndices = sheet => {
  const maxScan = Math.min(sheet.rowCount, 30)
  const { compCol, hdCol, lastHeaderRow } = scanRowsForColumns(sheet, maxScan)

  if (compCol == null || hdCol == null) {
    return null
  }

  const segmentCols = []
  for (let col = 1; col < hdCol; col++) {
    if (col !== compCol) {
      segmentCols.push(col)
    }
  }

  return {
    compCol,
    hdCol,
    segmentCols,
    headerRowNum: lastHeaderRow,
  }
}

const parseDataRow = (sheet, rowNum, columns, defaultBloco) => {
  const row = sheet.getRow(rowNum)
  const comp = cellValue(row.getCell(columns.compCol))
  const nrMedidor = cellValue(row.getCell(columns.hdCol))

  if (!comp && !nrMedidor) {
    return null
  }

  if (/^comp\.?$/i.test(comp)) {
    return null
  }

  if (comp.toUpperCase().trim() === "MACRO") {
    return { skipped: true, reason: "macro" }
  }

  const segments = columns.segmentCols.map(col =>
    cellValue(row.getCell(col))
  )
  const cdImovel = buildCdImovel(segments)
  const parsedComp = parseComplemento(comp, defaultBloco)

  const valid = parsedComp.valid && nrMedidor.length > 0

  return {
    rowNum,
    compOriginal: comp,
    nrMedidor,
    cdImovel,
    IM_COMPLEMENTO: parsedComp.IM_COMPLEMENTO || "",
    IM_BLOCO: parsedComp.IM_BLOCO || "",
    valid,
    error: !parsedComp.valid
      ? parsedComp.error
      : !nrMedidor
        ? "Nº HD vazio. Informe o número do hidrômetro na coluna Nº HD."
        : null,
  }
}

export const parseSpreadsheet = async (buffer, defaultBloco = "1") => {
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(buffer)

  const sheet = workbook.worksheets[0]
  if (!sheet) {
    return {
      rows: [],
      errors: [
        "Planilha vazia ou sem abas. Use um arquivo .xlsx com a lista de hidrômetros na primeira aba.",
      ],
    }
  }

  const columns = findColumnIndices(sheet)
  if (!columns) {
    return {
      rows: [],
      errors: [
        'Cabeçalho não encontrado na primeira aba. É preciso ter as colunas "COMP" (ou "Comp.") e "Nº HD" (ou "Medidor").',
      ],
    }
  }

  const rows = []
  const errors = []
  let skippedMacro = 0

  for (
    let rowNum = columns.headerRowNum + 1;
    rowNum <= sheet.rowCount;
    rowNum++
  ) {
    const parsed = parseDataRow(sheet, rowNum, columns, defaultBloco)
    if (!parsed) continue

    if (parsed.skipped) {
      if (parsed.reason === "macro") skippedMacro++
      continue
    }

    rows.push(parsed)
    if (!parsed.valid && parsed.error) {
      errors.push(`Linha ${rowNum}: ${parsed.error}`)
    }
  }

  const hdSeen = new Map()
  const compSeen = new Map()
  rows.forEach(row => {
    if (!row.valid) return
    const hdKey = String(row.nrMedidor).trim().toUpperCase()
    const compKey = `${String(row.IM_COMPLEMENTO).trim().toUpperCase()}|||${String(
      row.IM_BLOCO ?? "",
    ).trim()}`
    if (hdSeen.has(hdKey)) {
      row.valid = false
      row.error = `Nº HD duplicado na planilha (${row.nrMedidor}). Já aparece na linha ${hdSeen.get(
        hdKey,
      )}.`
      errors.push(`Linha ${row.rowNum}: ${row.error}`)
    } else {
      hdSeen.set(hdKey, row.rowNum)
    }
    if (!row.valid) return
    if (compSeen.has(compKey)) {
      row.valid = false
      row.error = `COMP duplicado na planilha (${row.compOriginal}). Já aparece na linha ${compSeen.get(
        compKey,
      )}.`
      errors.push(`Linha ${row.rowNum}: ${row.error}`)
    } else {
      compSeen.set(compKey, row.rowNum)
    }
  })

  if (rows.length === 0) {
    errors.push(
      skippedMacro > 0
        ? "Nenhuma linha de AP encontrada. Só havia MACRO, que é ignorada na importação."
        : "Nenhuma linha de dados encontrada após o cabeçalho COMP / Nº HD.",
    )
  }

  return { rows, errors, skippedMacro }
}

export const buildImovelKey = (comp, bloco) => {
  const normalizedComp = String(comp || "")
    .trim()
    .toUpperCase()
  const normalizedBloco = String(bloco ?? "").trim()
  return `${normalizedComp}|||${normalizedBloco}`
}

export const buildCdImovelKey = cd => `cd|||${String(cd || "").trim()}`

const addCompBlocoVariants = (keys, comp, bloco) => {
  if (!comp) return
  keys.add(buildImovelKey(comp, bloco))
  keys.add(buildImovelKey(comp, null))
  keys.add(buildImovelKey(comp, ""))
}

export const getStoredImovelMatchKeys = elem => {
  const keys = new Set()
  const comp = String(elem.IM_COMPLEMENTO || "").trim()
  const bloco = elem.IM_BLOCO

  if (comp) {
    addCompBlocoVariants(keys, comp, bloco)

    if (/^\d+[A-Z]?$/i.test(comp) && bloco) {
      addCompBlocoVariants(keys, `APT${bloco} ${comp}`, bloco)
    }

    const aptoMatch = comp.match(/^APTO\s+(\d+)$/i)
    if (aptoMatch) {
      addCompBlocoVariants(keys, `AP${aptoMatch[1]}`, bloco)
    }

    const apOnlyMatch = comp.match(/^AP(\d+)$/i)
    if (apOnlyMatch) {
      addCompBlocoVariants(keys, `APTO ${apOnlyMatch[1]}`, bloco)
    }
  }

  if (elem.CD_IMOVEL) {
    keys.add(buildCdImovelKey(elem.CD_IMOVEL))
  }

  return keys
}

export const getRowMatchKeys = row => {
  const keys = new Set()
  if (!row.valid) return keys

  addCompBlocoVariants(keys, row.IM_COMPLEMENTO, row.IM_BLOCO)

  const aptoMatch = String(row.IM_COMPLEMENTO || "").match(/^APTO\s+(\d+)$/i)
  if (aptoMatch) {
    addCompBlocoVariants(keys, `AP${aptoMatch[1]}`, row.IM_BLOCO)
  }

  if (row.cdImovel) {
    keys.add(buildCdImovelKey(row.cdImovel))
  }

  return keys
}

export const isRowAlreadyRegistered = (row, existingKeys) => {
  if (!row.valid) return false
  return [...getRowMatchKeys(row)].some(key => existingKeys.has(key))
}
